import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { DragulaService } from 'ng2-dragula';
import { Router, NavigationStart, NavigationEnd, NavigationError, NavigationCancel, RoutesRecognized } from '@angular/router';
import { ClientService } from '../../services/client/client.service';
import { IoService } from '../../services/io/io.service';
import { LocationService } from '../../services/location/location.service';
import { AuthorizationService } from '../../services/authorization/authorization.service';
import { ChartConfiguration } from 'chart.js';
import { ReportService } from '../../services/report/report.service';
import { SupportService } from '../../services/support/support.service';
import { SettingsService } from '../../services/settings/settings.service';
import { ResponsiveService } from '../../services/responsive/responsive.service';
import { AppComponent } from '../../app.component';
import { DialogButtonStyle, DialogConfig, DialogOption } from '../shared/ui/dialog/dialog-options';

// Layout editing scope: the signed-in user's own dashboard, a user group's
// default, or the tenant-wide default (the last two need the admin permission).
export type DashboardScope = 'user' | 'userGroup' | 'global';

interface DashboardEditState {
  scope: DashboardScope;
  userGroupId?: string;
  label: string;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {

  private static readonly CATEGORY_GROUP = 'dashboard-categories';
  private static readonly ELEMENT_GROUP = 'dashboard-elements';

  widgetsIdIndex: any = {};
  widgets = [];
  pendingUpdates = [];

  dashboard: any;
  supportActive: boolean = false;
  settingsActive: boolean = false;

  // Right-click menu position (relative to the dashboard body) while open.
  contextMenu: { x: number, y: number } = null;
  // Set while the dashboard is in drag-and-drop edit mode.
  editing: DashboardEditState = null;
  // The scope being edited already has a saved layout (enables Reset to Default).
  editHasLayout = false;
  saving = false;
  isAdmin = false;
  private isMobile = false;
  private isMobileSub: Subscription;
  private permissionsSub: Subscription;

  constructor(
    private ioService: IoService,
    private locationService: LocationService,
    private authorizationService: AuthorizationService,
    private clientService: ClientService,
    private router: Router,
    private reportService: ReportService,
    private supportService: SupportService,
    private settingsService: SettingsService,
    private responsiveService: ResponsiveService,
    private dragulaService: DragulaService
  ) {
    // Sections drag by their header; tiles drag anywhere within a section and
    // may be dropped in any section (all tile containers share one group).
    // Both groups are inert outside edit mode.
    if (!this.dragulaService.find(DashboardComponent.CATEGORY_GROUP)) {
      this.dragulaService.createGroup(DashboardComponent.CATEGORY_GROUP, {
        direction: 'vertical',
        moves: (el, container, handle) => !!this.editing && !!(handle as HTMLElement).closest('.category-name'),
      });
    }
    if (!this.dragulaService.find(DashboardComponent.ELEMENT_GROUP)) {
      this.dragulaService.createGroup(DashboardComponent.ELEMENT_GROUP, {
        // Tiles wrap into rows on wide screens: horizontal picks before/after
        // by the hovered tile's x midpoint, which is right for a grid.
        direction: 'horizontal',
        moves: () => !!this.editing,
      });
    }
  }

  ngOnInit(): void {
    // this.loadWidgets();
    this.loadDashboard();
    this.supportService.supportActive$.subscribe((active) => {
      this.supportActive = active;
    });
    this.supportActive = this.supportService.isSupportActive();

    this.settingsService.settingsActive$.subscribe((active) => {
      this.settingsActive = active;
    });
    this.settingsActive = this.settingsService.isSettingsActive();

    this.isMobile = this.responsiveService.isMobile();
    this.isMobileSub = this.responsiveService.isMobile$.subscribe(m => this.isMobile = m);
    this.isAdmin = this.authorizationService.checkPermission('admin');
    // On a hard load the permission map can arrive after this screen renders.
    this.permissionsSub = this.authorizationService.permissionsLoadedObservable().subscribe(() => {
      this.isAdmin = this.authorizationService.checkPermission('admin');
    });
  }

  ngOnDestroy(): void {
    console.log('onDestroy');
    // this.stopUpdates();
    if (this.isMobileSub) { this.isMobileSub.unsubscribe(); }
    if (this.permissionsSub) { this.permissionsSub.unsubscribe(); }
  }

  // Loads the dashboard as the signed-in user sees it, or (while editing) as
  // the requested scope's audience sees it.
  loadDashboard = async (scopeInput: any = {}) => {
    console.log('loadDashboard');
    const dashboard: any = await this.ioService.post('/dashboard/getDashboard', scopeInput);
    console.log('dashboard', dashboard);

    // Newer servers return categories already arranged by the saved layout;
    // older ones only return flat elements, grouped here by first appearance.
    if (!Array.isArray(dashboard.categories)) {
      const categories = {};
      for (const element of dashboard.elements) {
        if (!categories[element.category]) {
          categories[element.category] = [];
        }
        categories[element.category].push(element);
      }
      dashboard.categories = Object.keys(categories).map((categoryKey) => ({
        name: categoryKey,
        elements: categories[categoryKey],
      }));
    }
    for (const category of dashboard.categories) {
      for (const element of category.elements) {
        if (!element.layoutKey) {
          element.layoutKey = element.type + ':' + element.key;
        }
      }
    }

    this.dashboard = dashboard;
    return dashboard;
  }

  // ─── Layout editing ─────────────────────────────────────────────

  onContextMenu(event: MouseEvent) {
    if (this.isMobile || this.editing) {
      return;
    }
    event.preventDefault();
    const body = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.contextMenu = {
      x: event.clientX - body.left,
      y: event.clientY - body.top,
    };
  }

  @HostListener('document:click')
  closeContextMenu() {
    this.contextMenu = null;
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.contextMenu = null;
  }

  // Enters edit mode for a scope, starting from what that scope's audience
  // currently sees (own layout, group default, global default, or natural order).
  async startEdit(scope: DashboardScope, group: any = null) {
    this.contextMenu = null;
    const scopeInput: any = { scope };
    if (scope === 'userGroup') {
      scopeInput.userGroupId = group._id;
    }
    try {
      const dashboard = await this.loadDashboard(scopeInput);
      const layout = dashboard.layout;
      this.editHasLayout = !!layout && layout.scope === scope
        && (scope !== 'userGroup' || String(layout.userGroupId) === String(group._id));
      this.editing = {
        scope,
        userGroupId: scope === 'userGroup' ? group._id : undefined,
        label: scope === 'user' ? 'your dashboard'
          : scope === 'userGroup' ? 'the default dashboard for ' + group.name
          : 'the global default dashboard',
      };
    } catch (err) {
      AppComponent.showSnack((err && err.error && err.error.msg) || 'Unable to open the dashboard editor.', 'Dismiss', 5000);
    }
  }

  async chooseGroupDefault() {
    this.contextMenu = null;
    let groups: any[] = [];
    try {
      groups = await this.ioService.post('/userGroup/getUserGroups', {}) as any[];
    } catch (err) {
      groups = [];
    }
    if (!groups || groups.length === 0) {
      AppComponent.showSnack('No user groups found.', 'Dismiss', 4000);
      return;
    }
    const dialogConfig = new DialogConfig();
    dialogConfig.title = 'Edit Group Default';
    dialogConfig.message = 'Choose a user group:';
    dialogConfig.stackOptions = true;
    dialogConfig.options = groups.map((g) => new DialogOption(g.name, g._id, DialogButtonStyle.Normal));
    AppComponent.showDialog(dialogConfig).afterClosed().subscribe((result) => {
      const group = result && result.value ? groups.find((g) => g._id === result.value) : null;
      if (group) {
        this.startEdit('userGroup', group);
      }
    });
  }

  private editPayload() {
    const payload: any = {
      scope: this.editing.scope,
      categories: this.dashboard.categories.map((category) => ({
        name: category.name,
        elements: category.elements.map((element) => element.layoutKey),
      })),
    };
    if (this.editing.scope === 'userGroup') {
      payload.userGroupId = this.editing.userGroupId;
    }
    return payload;
  }

  async saveEdit() {
    if (!this.editing || this.saving) {
      return;
    }
    this.saving = true;
    try {
      await this.ioService.post('/dashboard/saveDashboard', this.editPayload());
      AppComponent.showSnack('Dashboard saved.', 'OK', 3000);
      this.editing = null;
      await this.loadDashboard();
    } catch (err) {
      AppComponent.showSnack((err && err.error && err.error.msg) || 'Unable to save the dashboard.', 'Dismiss', 5000);
    } finally {
      this.saving = false;
    }
  }

  async cancelEdit() {
    this.editing = null;
    await this.loadDashboard();
  }

  resetEdit() {
    if (!this.editing) {
      return;
    }
    const label = this.editing.label;
    AppComponent.showMessage('Reset Dashboard', 'Remove the saved layout for ' + label + '? The next default will apply instead.', ['Reset', 'Cancel'])
      .afterClosed().subscribe(async (result) => {
        if (!result || result.value !== 'Reset') {
          return;
        }
        const payload: any = { scope: this.editing.scope };
        if (this.editing.scope === 'userGroup') {
          payload.userGroupId = this.editing.userGroupId;
        }
        try {
          await this.ioService.post('/dashboard/resetDashboard', payload);
          AppComponent.showSnack('Dashboard reset.', 'OK', 3000);
          this.editing = null;
          await this.loadDashboard();
        } catch (err) {
          AppComponent.showSnack((err && err.error && err.error.msg) || 'Unable to reset the dashboard.', 'Dismiss', 5000);
        }
      });
  }

  loadWidgets = async (widgetIds = null) => {
console.log('pendingUpdates', this.pendingUpdates);

    let widgets: any = await this.ioService.post('/widget/loadWidgets', {
      widgetIds,
      utcOffset: new Date().getTimezoneOffset(),
    });

    for (let widget of widgets) {
      if (widget.updateFrequency) {
        let pendingUpdate = setTimeout(() => {
          this.pendingUpdates.splice(this.pendingUpdates.indexOf(pendingUpdate), 1);
          this.loadWidgets([widget._id]);
        }, 1000 * widget.updateFrequency);
        this.pendingUpdates.push(pendingUpdate);
      }

      if (this.widgetsIdIndex[widget._id] == undefined) {
        this.widgetsIdIndex[widget._id] = this.widgets.length;
        this.widgets.push({});
      }

      if (widget.data) {
        if (widget.data.options) {
          let chartOptions: ChartConfiguration<'doughnut'>['options'] = widget.data.options;

          // let chartOptions: ChartOptions = widget.data.options;
          widget.data.options = chartOptions;
        }
      }

      this.widgets[this.widgetsIdIndex[widget._id]] = widget;
    };
  }

  widgetClick = async (widget) => {
    if (widget.data && widget.data.reportKey) {
      await this.reportService.navigateToReport(widget.data.reportKey);
    }
    // this.router.navigate([screen.path]);
  }

  stopUpdates = () => {
    console.log('stopUpdates');
    for (let pendingUpdate of this.pendingUpdates) {
      clearTimeout(pendingUpdate);
    }
    return;
  }

  loadReportWithContext = (element) => {
    if (this.editing) { return; }
    // console.log('loadReportWithContext', element);
    this.reportService.loadReportWithContext({
      name: element.name,
      key: element.key,
      reportId: element.reportId,
      activeFields: element.activeFields,
      filters: element.filters,
    });
  }

  startTransfer = (element) => {
    if (this.editing) { return; }
    console.log('LINK: ', element.url);
    this.router.navigate([element.url]);
  }

  startVT = (element) => {
    if (this.editing) { return; }
    console.log('VT LINK: ', element.url);
    this.router.navigate([element.url]);
  }

  openScreen = (element) => {
    if (this.editing) { return; }
    this.router.navigate([element.url]);
  }
}
